import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Flex, Text, Button, Badge, Spinner, Center, HStack, Input, InputGroup,
  InputLeftElement, Select, Table, Thead, Tbody, Tr, Th, Td, IconButton, Tooltip,
  useToast, Circle,
} from "@chakra-ui/react";
import { Phone, MessageSquare, Search, RefreshCw, Users, ChevronLeft, ChevronRight } from "lucide-react";
import { getCrmBase, criarCrmContato } from "../../services/api";
import { useSoftphone } from "../../contexts/softphone";
import { useDebounce } from "../../hooks/useDebounce";
import { formatPhone, paraDiscagem, formatBRL, soDigitos, saudeInfo, recenciaLabel } from "./crmUtils";

const ACCENT = "#F68633";
const PER_PAGE = 30;

export default function BaseTab({ onAbrirChat, onAbrirFicha, refreshKey }) {
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [vendedor, setVendedor] = useState("");
  const [saude, setSaude] = useState("");
  const [ordenar, setOrdenar] = useState("");
  const [page, setPage] = useState(1);
  const [abrindo, setAbrindo] = useState(null);
  const buscaDeb = useDebounce(search, 350);
  const sp = useSoftphone();
  const toast = useToast();

  const carregar = useCallback(async () => {
    setLoading(true);
    const r = await getCrmBase({
      search: buscaDeb || undefined,
      vendedor_id: vendedor || undefined,
      saude: saude || undefined,
      ordenar: ordenar || undefined,
      page,
      per_page: PER_PAGE,
    });
    setResp(r || null);
    setLoading(false);
  }, [buscaDeb, vendedor, saude, ordenar, page]);

  useEffect(() => { carregar(); }, [carregar, refreshKey]);
  // Volta à primeira página quando muda busca/filtro.
  useEffect(() => { setPage(1); }, [buscaDeb, vendedor, saude, ordenar]);

  const ligar = (c) => {
    if (soDigitos(c.telefone).length < 10) { toast({ status: "warning", title: "Sem telefone válido" }); return; }
    if (!sp.registered) { toast({ status: "warning", title: "Ramal desconectado", description: "Conecte o softphone no Discador." }); return; }
    sp.dial(paraDiscagem(c.telefone));
    toast({ status: "info", title: "Discando...", description: formatPhone(c.telefone) });
  };

  const conversar = async (c) => {
    if (soDigitos(c.telefone).length < 10) { toast({ status: "warning", title: "Sem telefone válido" }); return; }
    setAbrindo(c.cliente_id);
    const r = await criarCrmContato({ telefone: c.telefone, nome: c.nome, origem: "base" });
    setAbrindo(null);
    if (r?.data?.id) onAbrirChat(r.data.id);
    else toast({ status: "error", title: "Erro", description: r?.error || "Não foi possível abrir a conversa." });
  };

  const linhas = resp?.data || [];
  const pag = resp?.pagination;
  const periodo = resp?.rfm_periodo_dias;

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3} gap={2} flexWrap="wrap">
        <HStack spacing={2} flex="1" flexWrap="wrap">
          <InputGroup size="sm" maxW="280px">
            <InputLeftElement pointerEvents="none"><Search size={15} color="#999" /></InputLeftElement>
            <Input placeholder="Buscar por nome, documento ou telefone..." value={search} onChange={(e) => setSearch(e.target.value)} bg="white" />
          </InputGroup>
          <Select size="sm" maxW="200px" bg="white" placeholder="Todos vendedores" value={vendedor} onChange={(e) => setVendedor(e.target.value)}>
            <option value="sem">Sem vendedor</option>
            {(resp?.vendedores || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </Select>
          <Select size="sm" maxW="150px" bg="white" placeholder="Toda saúde" value={saude} onChange={(e) => setSaude(e.target.value)}>
            <option value="verde">Saudável</option>
            <option value="amarelo">Atenção</option>
            <option value="vermelho">Em risco</option>
            <option value="sem_compra">Sem compra</option>
          </Select>
          <Select size="sm" maxW="160px" bg="white" placeholder="Ordem padrão" value={ordenar} onChange={(e) => setOrdenar(e.target.value)}>
            <option value="valor_desc">Maior valor</option>
            <option value="valor_asc">Menor valor</option>
          </Select>
        </HStack>
        <Button size="sm" variant="ghost" leftIcon={<RefreshCw size={15} />} onClick={carregar}>Atualizar</Button>
      </Flex>

      {periodo && <Text fontSize="xs" color="gray.400" mb={2}>Frequência e valor consideram os últimos {periodo} dias.</Text>}

      {loading ? (
        <Center py={16}><Spinner color={ACCENT} /></Center>
      ) : linhas.length === 0 ? (
        <Center py={16} flexDirection="column" color="gray.400">
          <Users size={40} />
          <Text mt={3} fontSize="sm">Nenhum cliente encontrado.</Text>
        </Center>
      ) : (
        <>
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Cliente</Th>
                  <Th>Saúde</Th>
                  <Th>Última compra</Th>
                  <Th isNumeric>Pedidos</Th>
                  <Th isNumeric>Valor</Th>
                  <Th>Vendedor</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {linhas.map((c) => {
                  const s = saudeInfo(c.saude);
                  return (
                    <Tr key={c.cliente_id} _hover={{ bg: "orange.50", cursor: "pointer" }} onClick={() => onAbrirFicha(c.cliente_id)}>
                      <Td>
                        <Box minW={0}>
                          <HStack spacing={1}>
                            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{c.nome || "Sem nome"}</Text>
                            {c.tipo_doc && <Badge fontSize="9px" colorScheme={c.tipo_doc === "CNPJ" ? "purple" : "blue"}>{c.tipo_doc === "CNPJ" ? "PJ" : "PF"}</Badge>}
                          </HStack>
                          <Text fontSize="xs" color="gray.400" noOfLines={1}>{formatPhone(c.telefone) || "sem telefone"}</Text>
                        </Box>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Circle size="10px" bg={s.hex} />
                          <Text fontSize="xs" color="gray.600">{s.label}</Text>
                        </HStack>
                      </Td>
                      <Td><Text fontSize="xs" color="gray.600">{recenciaLabel(c.recencia_dias)}</Text></Td>
                      <Td isNumeric><Text fontSize="sm">{c.frequencia}</Text></Td>
                      <Td isNumeric><Text fontSize="sm" fontWeight="medium">{formatBRL(c.valor_periodo)}</Text></Td>
                      <Td>
                        {c.vendedor_nome
                          ? <Text fontSize="xs" color="gray.500" noOfLines={1}>{c.vendedor_nome}</Text>
                          : <Badge fontSize="9px" colorScheme="gray">sem vendedor</Badge>}
                      </Td>
                      <Td onClick={(e) => e.stopPropagation()}>
                        <HStack spacing={1} justify="end">
                          <Tooltip label="Ligar"><IconButton aria-label="Ligar" size="xs" variant="ghost" colorScheme="green" icon={<Phone size={15} />} onClick={() => ligar(c)} /></Tooltip>
                          <Tooltip label="Conversar"><IconButton aria-label="Conversar" size="xs" variant="ghost" colorScheme="orange" icon={<MessageSquare size={15} />} isLoading={abrindo === c.cliente_id} onClick={() => conversar(c)} /></Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {pag && (
            <Flex justify="space-between" align="center" mt={3} flexWrap="wrap" gap={2}>
              <Text fontSize="xs" color="gray.500">{pag.total.toLocaleString("pt-BR")} clientes · página {pag.page} de {pag.last_page}</Text>
              <HStack>
                <IconButton aria-label="Anterior" size="sm" variant="outline" icon={<ChevronLeft size={16} />} isDisabled={pag.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
                <IconButton aria-label="Próxima" size="sm" variant="outline" icon={<ChevronRight size={16} />} isDisabled={pag.page >= pag.last_page} onClick={() => setPage((p) => p + 1)} />
              </HStack>
            </Flex>
          )}
        </>
      )}
    </Box>
  );
}
